import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { BAGGAGE_PATH } from '../../../../config/paths';
import {
  clearBaggage,
  getBaggageV2,
  postAddBaggageComment,
} from '../../../../actions';
import ResourceProperty from '../../../common/resource/ResourceProperty';
import Loader from '../../../common/Loader';
import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';
import { Table } from '../../../common/Table';
import BaggageToolbar from './BaggageToolbar';
import {
  BAGGAGE_STATUS,
  KILOGRAM_SYMBOL,
  TRANSACTION_TYPE_CREDIT_CARD,
} from '../../../../config/constants';
import Content from '../../../layout/Content';
import NoDataResource from '../../../common/resource/NoDataResource';
import { tzNormalizeDate } from '../../../../utils/date';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import BaggageItemMovementResource from './resource/BaggageItemMovementResource';
import { numberFormatter } from '../../../../utils/number';
import EnumStatus from '../../../common/EnumStatus';
import {
  BAGGAGE_ITEM_COLUMNS,
  BAGGAGE_TICKET_COLUMNS,
} from '../../../../config/columns';
import CreditNote from '../../reservation/ticket/CreditNote';
import BaggageCommentForm from './BaggageCommentForm';

const Baggage = ({
  breadcrumbs,
  baggage,
  loading,
  match,
  dispatchGetBaggage,
  dispatchClearBaggage,
  dispatchPostAddBaggageComment,
}) => {
  const { id } = match.params;

  useEffect(() => {
    dispatchGetBaggage({ id });
    return () => {
      dispatchClearBaggage();
    };
  }, []);

  const handleBaggageCommentSubmit = async (formValues) => {
    const { baggageComment } = formValues;
    await dispatchPostAddBaggageComment({
      id,
      comment: baggageComment,
    });
    dispatchGetBaggage({ id });
  };

  let content;
  let toolbar;

  if (baggage && !baggage.isEmpty()) {
    const ticketList = baggage.get('ticketList');
    const baggageComments = baggage.get('baggageCommentList') || [];

    const tickets = (
      <div className="mb-3">
        <h3>Boletos</h3>
        <Table
          columns={BAGGAGE_TICKET_COLUMNS}
          data={ticketList}
          defaultPageSize={ticketList.length}
          showPagination={false}
        />
      </div>
    );

    const itemList = baggage.get('itemList');

    const baggageCommentSection = (
      <div className="mb-3">
        <h3>Comentarios del Equipaje</h3>
        <BaggageCommentForm
          comments={
            baggageComments.toJS ? baggageComments.toJS() : baggageComments
          }
          onSubmit={handleBaggageCommentSubmit}
        />
      </div>
    );

    const items = (
      <div className="mb-3">
        <h3>Items</h3>
        <Table
          columns={BAGGAGE_ITEM_COLUMNS}
          data={itemList}
          defaultPageSize={itemList.length}
          showPagination={false}
          rowComponent={BaggageItemMovementResource}
        />
      </div>
    );

    let excess = null;

    if (baggage.get('excessBaggage')) {
      const excessBaggage = baggage.get('excessBaggage');

      const {
        paymentMethod: { transactionCode },
        voucherCode,
      } = excessBaggage;

      let voucherCodeResource = null;
      if (transactionCode === TRANSACTION_TYPE_CREDIT_CARD)
        voucherCodeResource = (
          <ResourceProperty label="Nro Referencia (POS):">
            {voucherCode}
          </ResourceProperty>
        );

      const businessField = excessBaggage.transaction &&
        excessBaggage.transaction.business && (
          <div>
            <ResourceProperty label="RUC:">
              {excessBaggage.transaction.business.businessTaxId}
            </ResourceProperty>
            <ResourceProperty label="Razón Social:">
              {excessBaggage.transaction.business.name}
            </ResourceProperty>
          </div>
        );

      let contingencyMotive = null;
      if (excessBaggage !== null)
        contingencyMotive = (
          <>
            <ResourceProperty label="Motivo de Contingencia:">
              {excessBaggage.contingencyMotive}
            </ResourceProperty>
            <ResourceProperty label="Fecha de emisión de Contingencia:">
              {tzNormalizeDate({
                date: excessBaggage.contingencyAssignmentDocumentDate,
                format: DATE_TIME_FORMAT,
              })}
            </ResourceProperty>
          </>
        );

      excess = (
        <div>
          <h3>Exceso Equipaje</h3>
          <ResourceProperty label="Tipo de Documento:">
            {excessBaggage.transaction && excessBaggage.transaction.voucherType
              ? excessBaggage.transaction.voucherType.name
              : '-'}
          </ResourceProperty>
          {businessField}
          <ResourceProperty label="Número de Documento:">
            {excessBaggage.fullDocumentCode}
          </ResourceProperty>
          <ResourceProperty label="Pagador:">
            {excessBaggage.payerCustomer.fullName}
          </ResourceProperty>
          <ResourceProperty label="Peso:">
            {excessBaggage.weight} {KILOGRAM_SYMBOL}
          </ResourceProperty>
          <ResourceProperty label="Precio Total:">
            {numberFormatter({ value: excessBaggage.priceBeforeDiscount })}
          </ResourceProperty>
          <ResourceProperty label="Descuento:">
            {numberFormatter({ value: excessBaggage.discountAmount })}
          </ResourceProperty>
          <ResourceProperty label="Precio Final:">
            {numberFormatter({ value: excessBaggage.price })}
          </ResourceProperty>
          {excessBaggage.paymentMethodSummaryList &&
          excessBaggage.paymentMethodSummaryList.length > 1 ? (
            <>
              <ResourceProperty label="Metodos de Pago:" />
              {excessBaggage.paymentMethodSummaryList.map(
                (paymentMethodSummary) => (
                  <ResourceProperty
                    id={paymentMethodSummary.transactionCode}
                    label={`${paymentMethodSummary.paymentMethodName}: `}
                  >
                    {numberFormatter({ value: paymentMethodSummary.amount })}
                  </ResourceProperty>
                ),
              )}
            </>
          ) : (
            <ResourceProperty label="Método de Pago:">
              {excessBaggage.paymentMethod.name}
            </ResourceProperty>
          )}
          {voucherCodeResource}
          {contingencyMotive}
          <ResourceProperty label="Comentario:">
            {excessBaggage.comment}
          </ResourceProperty>
        </div>
      );
    }

    toolbar = (
      <BaggageToolbar
        baggageId={baggage.get('id')}
        baggage={baggage.toJS()}
        voided={baggage.get('baggageStatus') === BAGGAGE_STATUS.VOIDED.value}
      />
    );

    const creditNote = baggage.get('creditNote') && (
      <CreditNote
        description="Anticipo de Equipaje"
        creditNote={baggage.get('creditNote')}
      />
    );

    content = (
      <>
        {tickets}
        {items}
        <Row>
          <Col>
            <ResourceProperty label="Tipo de Servicio:">
              {baggage.get('serviceTypeName')}
            </ResourceProperty>
            <ResourceProperty label="Peso Total:">
              {baggage.get('totalWeight')} {KILOGRAM_SYMBOL}
            </ResourceProperty>
            <ResourceProperty label="Comentario:">
              {baggage.get('comment')}
            </ResourceProperty>
            <ResourceProperty label="Estado:">
              <EnumStatus
                enumObject={BAGGAGE_STATUS}
                enumValue={baggage.get('baggageStatus')}
              />
            </ResourceProperty>
            <ResourceProperty label="Usuario que Registró:">
              {baggage.get('userFullName')}
            </ResourceProperty>
            <ResourceProperty label="Fecha de Creación:">
              {tzNormalizeDate({
                date: baggage.get('createDate'),
                format: DATE_TIME_FORMAT,
              })}
            </ResourceProperty>
            {excess}
          </Col>
          <Col>{creditNote}</Col>
        </Row>
        <hr />
        {baggageCommentSection}
      </>
    );
  } else if (loading) {
    content = <Loader />;
  } else {
    content = <NoDataResource returnPage={BAGGAGE_PATH} />;
  }

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Equipaje"
      toolbar={toolbar}
      content={content}
    />
  );
};

Baggage.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchGetBaggage: PropTypes.func.isRequired,
  dispatchClearBaggage: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  baggage: PropTypes.instanceOf(Immutable.Map).isRequired,
  dispatchPostAddBaggageComment: PropTypes.func.isRequired,
};

Baggage.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ BaggageUnit }) => ({
  breadcrumbs: [
    ...BaggageUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Equipajes y Excesos',
      href: BAGGAGE_PATH,
    },
    {
      text: 'Ver',
      href: '',
    },
  ],
  baggage: BaggageUnit.Baggage.getIn(['current', 'content']),
  loading: !BaggageUnit.Baggage.getIn(['current', 'activity']).isEmpty(),
});

const mapDispatchToProps = {
  dispatchGetBaggage: getBaggageV2,
  dispatchClearBaggage: clearBaggage,
  dispatchPostAddBaggageComment: postAddBaggageComment,
};

export default connect(mapStateToProps, mapDispatchToProps)(Baggage);
